import styled from 'styled-components';
import COLORS from '../../utils/Colors';
import { NAVBAR_HEIGHT } from '../../utils/constants';

// ============================================
// Layout Components
// ============================================

export const PageWrapper = styled.div`
    padding-top: ${NAVBAR_HEIGHT};
    min-height: 100vh;
    background-color: ${COLORS.freshaGray50};
`;

export const HeroSection = styled.section`
    text-align: center;
    padding: 60px 24px 40px;
    background-color: ${COLORS.white};
`;

export const AppShowcaseSection = styled.section`
    display: flex;
    justify-content: center;
    padding: 40px 24px 80px;
    background-color: ${COLORS.white};
`;

export const FormSection = styled.section`
    display: flex;
    justify-content: center;
    padding: 80px 24px;
    background-color: ${COLORS.freshaGray50};
`;

// ============================================
// Hero Components
// ============================================

export const HeroTitle = styled.h1`
    font-size: 3rem;
    font-weight: 700;
    color: ${COLORS.freshaBlack};
    margin: 0 0 16px 0;
    line-height: 1.2;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

export const HeroSubtitle = styled.p`
    font-size: 1.25rem;
    color: ${COLORS.freshaGray600};
    margin: 0;
    max-width: 700px;
    margin: 0 auto;
    line-height: 1.5;

    @media (max-width: 768px) {
        font-size: 1rem;
    }
`;

// ============================================
// App Card Components
// ============================================

export const AppCard = styled.div`
    background: linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%);
    border-radius: 24px;
    padding: 48px;
    max-width: 600px;
    width: 100%;
    position: relative;
    overflow: hidden;
    min-height: 500px;
    display: flex;
    flex-direction: column;

    @media (max-width: 768px) {
        padding: 32px 24px;
        min-height: auto;
    }
`;

export const AppCardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.875rem;
    color: ${COLORS.freshaGray700};
    margin-bottom: 16px;

    svg {
        width: 16px;
        height: 16px;
    }
`;

export const AppCardTitle = styled.h2`
    font-size: 2.5rem;
    font-weight: 700;
    color: ${COLORS.freshaBlack};
    margin: 0 0 16px 0;
    line-height: 1.2;

    @media (max-width: 768px) {
        font-size: 1.75rem;
    }
`;

export const AppCardSubtitle = styled.p`
    font-size: 1rem;
    color: ${COLORS.freshaGray700};
    margin: 0 0 24px 0;
    line-height: 1.6;
    max-width: 400px;
`;

export const StoreBadges = styled.div`
    display: flex;
    gap: 12px;
    margin-bottom: 32px;
    flex-wrap: wrap;
`;

export const StoreBadge = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background-color: ${COLORS.freshaBlack};
    color: ${COLORS.white};
    padding: 10px 16px;
    border-radius: 8px;
    text-decoration: none;
    font-size: 0.875rem;
    font-weight: 500;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.85;
    }

    svg {
        width: 20px;
        height: 20px;
    }
`;

export const StoreBadgeText = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;

export const StoreBadgeSmall = styled.span`
    font-size: 0.625rem;
    font-weight: 400;
    opacity: 0.8;
`;

export const StoreBadgeLarge = styled.span`
    font-size: 0.875rem;
    font-weight: 600;
`;

// ============================================
// Phone Mockup Components
// ============================================

export const PhoneMockupContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: auto;
`;

export const PhoneMockup = styled.div`
    width: 280px;
    height: 400px;
    background-color: ${COLORS.white};
    border-radius: 32px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    padding: 12px;
    position: relative;

    @media (max-width: 768px) {
        width: 240px;
        height: 340px;
    }
`;

export const PhoneMockupScreen = styled.div`
    background-color: ${COLORS.freshaGray50};
    border-radius: 24px;
    height: 100%;
    padding: 16px;
    overflow: hidden;
`;

export const MockupHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
`;

export const MockupDate = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 1rem;
    font-weight: 600;
    color: ${COLORS.freshaBlack};

    svg {
        width: 16px;
        height: 16px;
    }
`;

export const MockupIcons = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
        width: 20px;
        height: 20px;
        color: ${COLORS.freshaGray500};
    }
`;

export const EmployeeRow = styled.div`
    display: flex;
    justify-content: center;
    gap: 24px;
    margin-bottom: 20px;
`;

export const EmployeeItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
`;

export const EmployeeAvatar = styled.div<{ $bgColor: string }>`
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background-color: ${props => props.$bgColor};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    font-weight: 600;
    color: ${COLORS.white};

    @media (max-width: 768px) {
        width: 40px;
        height: 40px;
        font-size: 1rem;
    }
`;

export const EmployeeName = styled.span`
    font-size: 0.75rem;
    color: ${COLORS.freshaGray600};
`;

export const MockupCalendar = styled.div`
    background-color: ${COLORS.white};
    border-radius: 12px;
    padding: 12px;
`;

export const CalendarRow = styled.div`
    display: flex;
    gap: 4px;
    margin-bottom: 8px;

    &:last-child {
        margin-bottom: 0;
    }
`;

export const CalendarSlot = styled.div<{ $isBooked?: boolean; $color?: string }>`
    flex: 1;
    height: 24px;
    border-radius: 4px;
    background-color: ${props => props.$isBooked ? props.$color || COLORS.mainTeal : COLORS.freshaGray100};
`;

export const TimeLabel = styled.div`
    font-size: 0.625rem;
    color: ${COLORS.freshaGray500};
    margin-bottom: 4px;
`;

// ============================================
// Form Components
// ============================================

export const FormCard = styled.div`
    background-color: ${COLORS.white};
    border-radius: 16px;
    padding: 48px;
    max-width: 700px;
    width: 100%;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);

    @media (max-width: 768px) {
        padding: 32px 24px;
    }
`;

export const FormTitle = styled.h2`
    font-size: 1.75rem;
    font-weight: 700;
    color: ${COLORS.freshaBlack};
    margin: 0 0 32px 0;
    text-align: center;

    @media (max-width: 768px) {
        font-size: 1.5rem;
    }
`;

export const FormGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 16px;
    }
`;

export const FormField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;

    &.full-width {
        grid-column: 1 / -1;
    }
`;

export const Label = styled.label`
    font-size: 0.875rem;
    font-weight: 500;
    color: ${COLORS.freshaGray700};
`;

export const SubmitButton = styled.button`
    width: 100%;
    max-width: 350px;
    height: 52px;
    margin: 40px auto 0;
    display: block;
    background-color: ${COLORS.mainTealDark};
    color: ${COLORS.white};
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: ${COLORS.mainTeal};
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
    }
`;

// ============================================
// Legacy exports (for backwards compatibility if needed)
// ============================================

export const PageContainer = PageWrapper;
export const Container = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;
export const FormContainer = FormCard;
export const Title = styled.div`
    text-align: center;
    margin-bottom: 2rem;
`;
export const TitleText = HeroTitle;
export const InputsRow = FormGrid;
export const BtnContainer = styled.div`
    text-align: center;
    margin-top: 40px;
`;
export const Btn = SubmitButton;
